import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  CircularProgress,
  Button,
  Stack,
  Alert,
  Pagination,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { formatDateTimeSecondsSk } from "app/utils/dateFormat";
import { getCreatedOrdersLogs } from "app/services/api/createdOrdersLogs";
import CreatedOrderLogDetailModal from "./CreatedOrderLogDetailModal";

const DEFAULT_LIMIT = 50;

const getRowId = (row) => row._id ?? row.id ?? row.orderId ?? JSON.stringify(row);

const getCreatedAt = (item) => {
  if (item.createdAt) return item.createdAt;
  if (item.data && item.data.createdAt) return item.data.createdAt;
  // Fallback: záznam nemá uložený createdAt, ale Mongo ObjectId má v prvých 4 bajtoch
  // zabudovaný čas vzniku dokumentu (unix sekundy) — použijeme ho ako čas vytvorenia.
  const id = item._id ?? item.id;
  if (typeof id === "string" && /^[0-9a-fA-F]{24}$/.test(id)) {
    const seconds = parseInt(id.substring(0, 8), 16);
    if (seconds > 0) return new Date(seconds * 1000).toISOString();
  }
  return null;
};

// Záznam "dsok" je sám snapshot objednávky; ostatné majú objednávku z dsokOrders v `order`.
const getOrderDoc = (item) =>
  item?.destination === "dsok" ? item?.data ?? null : item?.order ?? null;

// Log bez pripojenej objednávky, aby sa v detaile logu nemiešali dva dokumenty.
const getLogDoc = (item) => {
  if (!item) return null;
  const { order, ...log } = item;
  return log;
};

const CreatedOrdersLogsPage = () => {
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [page, setPage] = useState(1);

  const [orderId, setOrderId] = useState("");
  const [destination, setDestination] = useState("");
  const [createdAtFrom, setCreatedAtFrom] = useState("");
  const [createdAtTo, setCreatedAtTo] = useState("");

  const [detailItem, setDetailItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [orderItem, setOrderItem] = useState(null);
  const [orderOpen, setOrderOpen] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    const offset = (page - 1) * limit;

    const createdAtFromIso = createdAtFrom
      ? new Date(createdAtFrom).toISOString()
      : undefined;
    const createdAtToIso = createdAtTo
      ? new Date(createdAtTo).toISOString()
      : undefined;

    try {
      const response = await getCreatedOrdersLogs({
        limit,
        offset,
        orderId: orderId.trim() || undefined,
        destination: destination.trim() || undefined,
        createdAtFrom: createdAtFromIso,
        createdAtTo: createdAtToIso,
      });

      const data = response?.data;
      const list = Array.isArray(data?.items) ? data.items : [];
      const totalCount =
        data?.pagination?.total ??
        (typeof data?.total === "number" ? data.total : null) ??
        list.length;

      setItems(list);
      setTotal(totalCount);
    } catch (err) {
      console.error("Created orders logs fetch error:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("pages.createdOrdersLogs.errors.fetchFailed"),
      );
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, orderId, destination, createdAtFrom, createdAtTo, t]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const hasActiveFilters = orderId || destination || createdAtFrom || createdAtTo;

  const handleReset = () => {
    setOrderId("");
    setDestination("");
    setCreatedAtFrom("");
    setCreatedAtTo("");
    setPage(1);
  };

  const handleApply = () => {
    setPage(1);
    if (page === 1) fetchLogs();
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const handleView = (item) => {
    setDetailItem(item);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setDetailItem(null);
  };

  const handleViewOrder = (item) => {
    setOrderItem(item);
    setOrderOpen(true);
  };

  const handleCloseOrder = () => {
    setOrderOpen(false);
    setOrderItem(null);
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs
          sx={{
            "& a": { color: "text.secondary", textDecoration: "none" },
            "& a:hover": { textDecoration: "underline" },
          }}
        >
          <Link to="/logs">{t("pages.logs.title")}</Link>
          <Typography color="text.primary">
            {t("pages.createdOrdersLogs.title")}
          </Typography>
        </Breadcrumbs>
        <Typography variant="h2">
          {t("pages.createdOrdersLogs.title")}
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="wrap"
          >
            <TextField
              size="small"
              label={t("pages.createdOrdersLogs.filters.orderId")}
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              sx={{ minWidth: 140 }}
            />
            <TextField
              size="small"
              label={t("pages.createdOrdersLogs.filters.destination", { defaultValue: "Zdroj" })}
              placeholder="woocommerce, dsok, dotykacka…"
              value={destination}
              onChange={(e) => {
                setDestination(e.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              type="datetime-local"
              label={t("pages.createdOrdersLogs.filters.dateFrom")}
              value={createdAtFrom}
              onChange={(e) => {
                setCreatedAtFrom(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              size="small"
              type="datetime-local"
              label={t("pages.createdOrdersLogs.filters.dateTo")}
              value={createdAtTo}
              onChange={(e) => {
                setCreatedAtTo(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            {hasActiveFilters && (
              <Button
                variant="text"
                size="small"
                onClick={handleReset}
                disabled={loading}
              >
                {t("pages.createdOrdersLogs.filters.reset")}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleApply}
              disabled={loading}
            >
              {t("pages.createdOrdersLogs.filters.apply")}
            </Button>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
          >
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={limit}
                onChange={handleLimitChange}
                disabled={loading}
              >
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              disabled={loading}
            />
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchLogs}>
              {t("pages.createdOrdersLogs.buttons.retry")}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("pages.createdOrdersLogs.table.createdAt")}
                </TableCell>
                <TableCell>
                  {t("pages.createdOrdersLogs.table.destination", { defaultValue: "Zdroj" })}
                </TableCell>
                <TableCell>
                  {t("pages.createdOrdersLogs.table.orderId")}
                </TableCell>
                <TableCell align="right" width={110}>
                  {t("pages.createdOrdersLogs.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t("pages.createdOrdersLogs.empty.noData")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={getRowId(item)} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDateTimeSecondsSk(getCreatedAt(item))}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{item.destination ?? "—"}</TableCell>
                    <TableCell>{item.orderId ?? "—"}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip
                        title={t("pages.createdOrdersLogs.buttons.viewLog")}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleView(item)}
                          aria-label={t("pages.createdOrdersLogs.buttons.viewLog")}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          getOrderDoc(item)
                            ? t("pages.createdOrdersLogs.buttons.viewOrder")
                            : t("pages.createdOrdersLogs.buttons.orderNotFound")
                        }
                      >
                        <Box component="span">
                          <IconButton
                            size="small"
                            disabled={!getOrderDoc(item)}
                            onClick={() => handleViewOrder(item)}
                            aria-label={t("pages.createdOrdersLogs.buttons.viewOrder")}
                          >
                            <ReceiptLongOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CreatedOrderLogDetailModal
        open={detailOpen}
        onClose={handleCloseDetail}
        item={getLogDoc(detailItem)}
        title={t("pages.createdOrdersLogs.modal.title")}
      />

      <CreatedOrderLogDetailModal
        open={orderOpen}
        onClose={handleCloseOrder}
        item={getOrderDoc(orderItem)}
        title={t("pages.createdOrdersLogs.modal.orderTitle")}
        fileName={`order-${orderItem?.orderId || "unknown"}`}
      />
    </Box>
  );
};

export default CreatedOrdersLogsPage;
